// frontend/packages/business-console/src/pages/Invoices/InvoicePaymentPanel.jsx
//
// Right-rail payment-status panel. Renders the CrmPayment rows for
// this invoice (sourced from `GET /v2/crm/invoices/:id/payments`, work
// item 2.12). Webhook-driven state per design doc §3.6 — the panel
// never calls Stripe directly.
//
// Rendering rules:
//   - Each succeeded payment: amount + method + state chip + paidAt
//   - Refunds nested under the parent payment with red minus prefix
//   - Failed: muted strikethrough + reason
//   - Disputed: red banner at the top + chip on the row (link to
//     Stripe Dashboard would land later; v1 just flags it visually)

import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';

import { createPaymentRefund } from '../../services/invoiceService';

function sumRefunds(refunds) {
  return (Array.isArray(refunds) ? refunds : []).reduce(
    (s, r) => s + (r.amount || 0),
    0
  );
}

function fmtUsd(cents) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format((cents || 0) / 100);
}

function formatDateTime(input) {
  if (!input) return null;
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const STATE_COLOR = {
  pending: 'default',
  succeeded: 'success',
  failed: 'error',
  refunded: 'warning',
  partially_refunded: 'warning',
  disputed: 'error',
};

const METHOD_LABEL = {
  card: 'Card',
  ach: 'ACH',
  cash: 'Cash',
  check: 'Check',
  manual: 'Manual',
};

function methodLabel(p) {
  return METHOD_LABEL[p.paymentMethod] || p.paymentMethod || '—';
}

export default function InvoicePaymentPanel({
  payments,
  invoiceId,
  onRefundIssued,
}) {
  const list = Array.isArray(payments) ? payments : [];
  const hasDispute = list.some((p) => p.state === 'disputed');
  const [toast, setToast] = useState(null);

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: 'text.secondary',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          display: 'block',
          mb: 1,
        }}
      >
        Payments
      </Typography>

      {hasDispute ? (
        <Alert
          severity="error"
          icon={<WarningAmberRoundedIcon fontSize="small" />}
          sx={{ mb: 1.5 }}
        >
          Dispute opened on this invoice. Respond in your Stripe Dashboard.
        </Alert>
      ) : null}

      {list.length === 0 ? (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ color: 'text.secondary', py: 1 }}
        >
          <PaymentsOutlinedIcon fontSize="small" sx={{ opacity: 0.6 }} />
          <Typography variant="caption">
            No payments yet. Once the customer pays, the receipt will land here.
          </Typography>
        </Stack>
      ) : (
        <Stack divider={<Divider />} spacing={0}>
          {list.map((p) => (
            <PaymentRow
              key={p._id}
              payment={p}
              invoiceId={invoiceId}
              onRefundIssued={onRefundIssued}
              onToast={setToast}
            />
          ))}
        </Stack>
      )}

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            variant="filled"
            onClose={() => setToast(null)}
          >
            {toast.msg}
          </Alert>
        ) : null}
      </Snackbar>
    </Paper>
  );
}

function PaymentRow({ payment, invoiceId, onRefundIssued, onToast }) {
  const isFailed = payment.state === 'failed';
  const isPending = payment.state === 'pending';
  const stateColor = STATE_COLOR[payment.state] || 'default';

  const remaining = (payment.amount || 0) - sumRefunds(payment.refunds);
  const canRefund =
    Boolean(invoiceId) &&
    (payment.state === 'succeeded' || payment.state === 'partially_refunded') &&
    remaining > 0;

  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <Box sx={{ py: 1.25 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 1,
          opacity: isFailed ? 0.6 : 1,
          textDecoration: isFailed ? 'line-through' : 'none',
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              color: 'text.primary',
              fontSize: 14,
            }}
          >
            {fmtUsd(payment.amount)}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', display: 'block' }}
          >
            {methodLabel(payment)}
            {payment.paidAt
              ? `  ·  ${formatDateTime(payment.paidAt)}`
              : isPending
                ? '  ·  pending'
                : ''}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={payment.state}
          color={stateColor}
          variant="outlined"
          sx={{
            textTransform: 'capitalize',
            fontWeight: 600,
            fontSize: 10,
            height: 20,
          }}
        />
      </Box>

      {/* Failure reason */}
      {isFailed && (payment.failureMessage || payment.failureCode) ? (
        <Typography
          variant="caption"
          sx={{ color: 'error.main', display: 'block', mt: 0.5 }}
        >
          {payment.failureMessage || payment.failureCode}
        </Typography>
      ) : null}

      {/* Operator refund action (3.12) — succeeded / partially-refunded only */}
      {canRefund ? (
        <Box sx={{ mt: 0.75 }}>
          <Button
            size="small"
            variant="text"
            color="warning"
            startIcon={<ReplayOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={() => setDialogOpen(true)}
            sx={{ textTransform: 'none', fontSize: 12, py: 0.25 }}
          >
            Refund
            {remaining < (payment.amount || 0)
              ? ` (${fmtUsd(remaining)} left)`
              : ''}
          </Button>
        </Box>
      ) : null}

      {canRefund ? (
        <RefundDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          payment={payment}
          remaining={remaining}
          invoiceId={invoiceId}
          onToast={onToast}
          onRefundIssued={onRefundIssued}
        />
      ) : null}

      {/* Refunds nested in red with minus prefix */}
      {Array.isArray(payment.refunds) && payment.refunds.length > 0 ? (
        <Stack
          spacing={0.5}
          sx={{
            mt: 1,
            ml: 1,
            pl: 1,
            borderLeft: '2px solid',
            borderColor: 'warning.light',
          }}
        >
          {payment.refunds.map((r, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: 'warning.dark',
                    fontWeight: 600,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  −{fmtUsd(r.amount)} refunded
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {formatDateTime(r.refundedAt) || '—'}
                  {r.reason ? `  ·  ${r.reason}` : ''}
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>
      ) : null}
    </Box>
  );
}

function RefundDialog({
  open,
  onClose,
  payment,
  remaining,
  invoiceId,
  onToast,
  onRefundIssued,
}) {
  const [amountStr, setAmountStr] = useState((remaining / 100).toFixed(2));
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Reset the form whenever the dialog opens.
  React.useEffect(() => {
    if (open) {
      setAmountStr((remaining / 100).toFixed(2));
      setReason('');
      setError(null);
      setSubmitting(false);
    }
  }, [open, remaining]);

  const cents = Math.round(parseFloat(amountStr) * 100);
  const amountValid = Number.isFinite(cents) && cents > 0 && cents <= remaining;

  const handleSubmit = async () => {
    if (!amountValid) {
      setError(`Enter an amount between $0.01 and ${fmtUsd(remaining)}.`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await createPaymentRefund(invoiceId, payment._id, {
        amount: cents,
        reason: reason.trim() || undefined,
      });
      onClose();
      if (onToast) {
        onToast({
          severity: 'success',
          msg: `Refund of ${fmtUsd(
            cents
          )} issued — it'll appear here once Stripe confirms (a few seconds).`,
        });
      }
      // The charge.refunded webhook records the refund asynchronously; pull
      // fresh data now and again shortly to catch it.
      if (onRefundIssued) {
        onRefundIssued();
        setTimeout(() => onRefundIssued(), 4000);
      }
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          'Refund failed. Please try again.'
      );
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Refund payment</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          Refunding {fmtUsd(payment.amount)} payment · up to {fmtUsd(remaining)}{' '}
          available. The refund posts to the customer&apos;s original payment
          method.
        </Typography>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        <TextField
          label="Amount (USD)"
          value={amountStr}
          onChange={(e) => setAmountStr(e.target.value)}
          fullWidth
          size="small"
          inputMode="decimal"
          disabled={submitting}
          sx={{ mb: 2 }}
          error={Boolean(amountStr) && !amountValid}
          helperText={`Max ${fmtUsd(remaining)}`}
        />
        <TextField
          label="Reason (optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          size="small"
          multiline
          minRows={2}
          disabled={submitting}
          inputProps={{ maxLength: 500 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="warning"
          disabled={submitting || !amountValid}
          startIcon={
            submitting ? <CircularProgress size={14} color="inherit" /> : null
          }
        >
          {submitting
            ? 'Refunding…'
            : `Refund ${amountValid ? fmtUsd(cents) : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
